import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Sparkles, X, ArrowRight, Building2, Users2, CreditCard, Wrench } from "lucide-react";

const STEPS_BY_ROLE = {
  landlord: [
    { icon: Building2, title: "Add your first property", to: "/properties?add=1", desc: "30-second setup with address, photos, rent." },
    { icon: Users2, title: "Create a tenant lease", to: "/leases", desc: "Pre-filled by province in under 2 minutes." },
    { icon: CreditCard, title: "Publish payment instructions", to: "/settings/payment-instructions", desc: "Interac, bank details, or cheque — tenants pay you directly." },
    { icon: Sparkles, title: "Unlock smart features", to: "/billing", desc: "Try the full premium suite free for 30 days." },
  ],
  tenant: [
    { icon: CreditCard, title: "See your payment instructions", to: "/payments", desc: "Interac, bank, or cheque — pay your landlord directly." },
    { icon: Wrench, title: "Submit a maintenance request", to: "/tickets", desc: "Photo, description, done. Real-time updates." },
    { icon: Building2, title: "Use the rent calculator", to: "/rent-calculator", desc: "See exactly what you can afford." },
    { icon: Sparkles, title: "Build credit with rent receipts", to: "/wellness", desc: "Auto-generated CRA-ready receipts." },
  ],
};

export default function WelcomePackage() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!user?.user_id) return;
    const key = `crc_welcome_dismissed_${user.user_id}`;
    setDismissed(localStorage.getItem(key) === "1");
  }, [user?.user_id]);

  if (dismissed || !user?.role) return null;
  const steps = STEPS_BY_ROLE[user.role] || [];
  if (steps.length === 0) return null;

  const dismiss = () => {
    try { localStorage.setItem(`crc_welcome_dismissed_${user.user_id}`, "1"); } catch {}
    setDismissed(true);
  };

  const firstName = (user.name || "").split(" ")[0] || "there";

  return (
    <div data-testid="welcome-package" className="mb-7 p-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-sky-50 to-white text-slate-900 relative overflow-hidden shadow-sm">
      <button onClick={dismiss} data-testid="welcome-dismiss" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white text-slate-500 hover:text-slate-900 border border-slate-200">
        <X className="w-4 h-4"/>
      </button>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700">
        <Sparkles className="w-3 h-3"/> Welcome
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-bold mt-3 text-slate-900">
        Let's get you set up, {firstName}
      </h2>
      <p className="text-slate-600 mt-1 max-w-xl text-sm md:text-base">
        A short, four-step onboarding so your portfolio is ready in minutes. You can dismiss this any time.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <Link key={i} to={s.to} data-testid={`welcome-step-${i}`}
              className="group p-4 rounded-xl bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 flex items-center justify-center mb-3">
                <Icon className="w-4 h-4"/>
              </div>
              <div className="font-semibold text-sm leading-tight text-slate-900">{s.title}</div>
              {s.desc && <div className="text-xs text-slate-500 mt-1.5 leading-snug">{s.desc}</div>}
              <div className="text-xs text-emerald-700 mt-3 inline-flex items-center gap-1 font-semibold">Open <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5"/></div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
